<style lang="postcss">
	.field {
		position: relative;
		display: flex;
		flex-direction: column;
		container: field / inline-size;
		scroll-margin-bottom: 24px;
	}
</style>

<script lang="ts">
	import OneLineInputDisplay from "./OneLineInput/OneLineInputDisplay.svelte";
	import NpsDisplay from "./NPS/NPSDisplay.svelte";
	import RatingDisplay from "./Rating/RatingDisplay.svelte";
	import CreditCardDisplay from "./CreditCard/CreditCardDisplay.svelte";
	import PhoneDisplay from "./Phone/PhoneDisplay.svelte";
	import FileUploadDisplay from "./FileUpload/FileUploadDisplay.svelte";
	import SliderDisplay from "./Slider/SliderDisplay.svelte";
	import DropdownDisplay from "./Dropdown/DropdownDisplay.svelte";
	import TableEntryDisplay from "./TableEntry/TableEntryDisplay.svelte";
	import CommentDisplay from "./Comment/CommentDisplay.svelte";
	import RichTextDisplay from "./RichText/RichTextDisplay.svelte";
	import MultipleChoiceDisplay from "./MultipleChoice/MultipleChoiceDisplay.svelte";
	import EmailDisplay from "./Email/EmailDisplay.svelte";
	import HiddenDisplay from "./Hidden/HiddenDisplay.svelte";
	import LoaderField from "./LoaderField.svelte";
	import { slide } from "$lib/transitions/slide";
	import { onMount, onDestroy } from "svelte";
	import type { Writable } from "svelte/store";
	import { createEventDispatcher } from "svelte";
	import { getContext } from "svelte";
	import { BROWSER } from "esm-env";
	import { browser } from "$app/environment";

	export let field: FieldDisplay;

	let visit = getContext<Writable<Visit>>("visit");
	let currentField = getContext<Writable<CurrentField>>("currentField");
	let visitMeta = getContext<Writable<VisitMeta>>("visitMeta");
	let formValues = getContext<Writable<FormValues>>("formValues");
	let styling = getContext<Styling>("styling");
	let mouseEnterDelay: ReturnType<typeof setTimeout>;
	const remoteValidation =
		getContext<Writable<RemoteValidation[]>>("remoteValidation");
	$: remoteValidItem = $remoteValidation.find(
		(item) => item.field_id === field.id
	);

	const dispatch = createEventDispatcher();

	const isFieldBeingTracked = () => {
		return $visitMeta.trackingFields[field.id];
	};

	let fieldRef: HTMLDivElement;

	const fieldRefMouseEnter = () => {
		mouseEnterDelay = setTimeout(() => {
			startTrackingField("hover", 1500);
		}, 1500);
	};

	const fieldRefMouseleave = () => {
		if (mouseEnterDelay) {
			clearTimeout(mouseEnterDelay);
		}
		if ($currentField.action === "hover") {
			closeFieldTracking();
		}
	};

	onMount(() => {
		if (BROWSER) {
			fieldRef.addEventListener("mouseenter", fieldRefMouseEnter);
			fieldRef.addEventListener("mouseleave", fieldRefMouseleave);
		}
	});

	onDestroy(() => {
		if (fieldRef && BROWSER) {
			fieldRef.removeEventListener("mouseenter", fieldRefMouseEnter);
			fieldRef.removeEventListener("mouseleave", fieldRefMouseleave);
		}
		closeFieldTracking();
	});

	const closeFieldTracking = () => {
		if (BROWSER === false) return;
		if ($currentField.action && $currentField.tracking?.id === field.id) {
			$currentField = {
				action: null,
				tracking: null
			};
		}
		if (Object.keys($visitMeta.trackingFields).length === 0) return;
		for (const [field_id, time] of Object.entries($visitMeta.trackingFields)) {
			const timeSpent = Date.now() - time;
			if ($visit.fields[field_id]) {
				$visit.fields[field_id] += timeSpent;
			} else {
				$visit.fields[field_id] = timeSpent;
			}
		}
		$visitMeta.trackingFields = {};
	};

	const startTrackingField = (event: string, extra: number = 0) => {
		if (BROWSER === false) return;
		if (isFieldBeingTracked()) return;
		closeFieldTracking();
		$currentField.action = event;
		$currentField.tracking = field;
		if (!$visit.start) {
			$visitMeta.start = Date.now();
			$visit.start = true;
		}
		if ($visit.last_field !== field.id) $visit.last_field = field.id;
		if (!$visitMeta.trackingFields[field.id]) {
			$visitMeta.trackingFields[field.id] = Date.now();
		}
	};
	const updateOther = (e: any) => {
		if (typeof e.detail.active !== "undefined") {
			if (e.detail.active) {
				if (mouseEnterDelay) {
					clearTimeout(mouseEnterDelay);
				}
				startTrackingField("focus");
			} else {
				closeFieldTracking();
			}
		}
		if (browser) {
			setTimeout(() => {
				dispatch("updateOther", {
					...e.detail,
					field_id: field.id
				});
			}, 150);
		}
	};
	$: is_required =
		"requiredComputed" in field
			? field.requiredComputed
			: "settings" in field && "required" in field.settings
				? field.settings?.required
				: false;
	$: hiddenLabel =
		"settings" in field && "hideLabel" in field.settings
			? (field.settings?.hideLabel ?? false)
			: false;
</script>

<div
	transition:slide="{{ adaptiveDuration: true, duration: 300 }}"
	bind:this="{fieldRef}"
	class:field-nomargin="{field.noMargin && $visit.test === false}"
	class:field-nolabel="{hiddenLabel}"
	class:field-asterisk="{is_required && styling.showAsterisks}"
	class:invalid="{('valid' in field && !field.valid && field.tainted) ||
		(remoteValidItem &&
			'state' in remoteValidItem &&
			remoteValidItem.state === 'invalid')}"
	data-field-id="{field.id}"
	style="grid-column: span {field.columnSpan} / span {field.columnSpan}"
	class="field field-{field.type.toLowerCase()} {field.classes ?? ''}">
	{#if field.type === "OneLineInput"}
		<OneLineInputDisplay
			bind:field="{field}"
			bind:value="{$formValues[field.id]}"
			on:updateOther="{updateOther}" />
	{:else if field.type === "NPS"}
		<NpsDisplay
			bind:field="{field}"
			bind:value="{$formValues[field.id]}"
			on:updateOther="{updateOther}" />
	{:else if field.type === "Rating"}
		<RatingDisplay
			bind:field="{field}"
			bind:value="{$formValues[field.id]}"
			on:updateOther="{updateOther}" />
	{:else if field.type === "CreditCard"}
		<CreditCardDisplay bind:field="{field}" on:updateOther="{updateOther}" />
	{:else if field.type === "Phone"}
		<PhoneDisplay
			bind:field="{field}"
			bind:value="{$formValues[field.id]}"
			on:updateOther="{updateOther}" />
	{:else if field.type === "FileUpload"}
		<FileUploadDisplay
			bind:field="{field}"
			bind:value="{$formValues[field.id]}"
			on:updateOther="{updateOther}" />
	{:else if field.type === "Dropdown"}
		<DropdownDisplay
			bind:field="{field}"
			bind:value="{$formValues[field.id]}"
			on:updateOther="{updateOther}" />
	{:else if field.type === "Slider"}
		<SliderDisplay
			bind:field="{field}"
			bind:value="{$formValues[field.id]}"
			on:updateOther="{updateOther}" />
	{:else if field.type === "TableEntry"}
		<TableEntryDisplay
			bind:field="{field}"
			bind:value="{$formValues[field.id]}"
			on:updateOther="{updateOther}" />
	{:else if field.type === "Comment"}
		<CommentDisplay
			bind:field="{field}"
			bind:value="{$formValues[field.id]}"
			on:updateOther="{updateOther}" />
	{:else if field.type === "RichText"}
		<RichTextDisplay bind:field="{field}" on:updateOther="{updateOther}" />
	{:else if field.type === "MultipleChoice"}
		<MultipleChoiceDisplay
			bind:field="{field}"
			bind:value="{$formValues[field.id]}"
			on:updateOther="{updateOther}" />
	{:else if field.type === "Email"}
		<EmailDisplay
			bind:field="{field}"
			bind:value="{$formValues[field.id]}"
			on:updateOther="{updateOther}" />
	{:else if field.type === "Date"}
		{#await import("./Date/DateDisplay.svelte")}
			<LoaderField field="{field}" />
		{:then c}
			<svelte:component
				this="{c.default}"
				bind:field="{field}"
				bind:value="{$formValues[field.id]}"
				on:updateOther="{updateOther}" />
		{/await}
	{:else if field.type === "Calendly"}
		{#await import("./Calendly/CalendlyDisplay.svelte")}
			<LoaderField field="{field}" />
		{:then c}
			<svelte:component
				this="{c.default}"
				bind:field="{field}"
				bind:value="{$formValues[field.id]}"
				on:updateOther="{updateOther}" />
		{/await}
	{:else if field.type === "Address"}
		{#await import("./Address/AddressDisplay.svelte")}
			<LoaderField field="{field}" />
		{:then c}
			<svelte:component
				this="{c.default}"
				bind:field="{field}"
				bind:value="{$formValues[field.id]}"
				on:updateOther="{updateOther}" />
		{/await}
	{:else if field.type === "Time"}
		{#await import("./Time/TimeDisplay.svelte")}
			<LoaderField field="{field}" />
		{:then c}
			<svelte:component
				this="{c.default}"
				bind:field="{field}"
				bind:value="{$formValues[field.id]}"
				on:updateOther="{updateOther}" />
		{/await}
	{:else if field.type === "Hidden"}
		<HiddenDisplay bind:field="{field}" />
	{/if}
</div>
